import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  FiberManualRecord as DotIcon,
  ZoomIn as ZoomIcon,
  MyLocation as PanIcon,
  FlashOn as FlashIcon,
  GpsFixed as FollowIcon,
  Streetview as StreetViewIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useWebMapContext } from '../webMap/WebMapContext';
import type { WidgetDataBinding, DeviceListWidgetConfig, DeviceListAction, DeviceFieldFormatRule } from './types';

function relativeTime(ts: unknown): string {
  if (ts == null) return '';
  const d = new Date(String(ts));
  if (isNaN(d.getTime())) return String(ts);
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return d.toLocaleDateString();
}

function ageColor(ts: unknown): string {
  if (ts == null) return 'text.disabled';
  const d = new Date(String(ts));
  if (isNaN(d.getTime())) return 'text.disabled';
  const min = (Date.now() - d.getTime()) / 60000;
  if (min < 1) return '#4caf50';
  if (min < 5) return '#8bc34a';
  if (min < 30) return '#ff9800';
  return '#f44336';
}

function badgeColor(type: string): string {
  const t = type.toLowerCase();
  if (t.includes('phone')) return '#2e7d32';
  if (t.includes('watch')) return '#43a047';
  if (t.includes('vehicle')) return '#ef6c00';
  if (t.includes('sensor')) return '#5c6bc0';
  return '#546e7a';
}

/** Resolve a possibly dot-notated path against a properties bag, parsing stringified JSON along the way. */
function resolveField(props: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let cur: unknown = props;
  for (const part of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    let val = (cur as Record<string, unknown>)[part];
    if (val === undefined && parts.length > 1) {
      // try parsing parent if it's a JSON string
      const parentKey = parts[0];
      const raw = props[parentKey];
      if (typeof raw === 'string' && raw.startsWith('{')) {
        try {
          const parsed = JSON.parse(raw);
          props[parentKey] = parsed;
          val = (parsed as Record<string, unknown>)[part];
        } catch { /* not JSON */ }
      }
    }
    if (typeof val === 'string' && val.startsWith('{')) {
      try { val = JSON.parse(val); } catch { /* not JSON */ }
    }
    cur = val;
  }
  return cur;
}

const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function formatValue(v: unknown): string {
  if (v == null) return '';
  const s = String(v);
  if (ISO_RE.test(s)) {
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    }
  }
  return s;
}

/** Replace {fieldName} or {field.subkey} placeholders with feature property values. */
function renderTemplate(template: string, props: Record<string, unknown>): string {
  return template.replace(/\{([\w.]+)\}/g, (_, path) => {
    const v = resolveField(props, path);
    return formatValue(v);
  });
}

function elapsedTimeLabel(ts: unknown): string {
  if (ts == null) return '';
  const d = new Date(String(ts));
  if (isNaN(d.getTime())) return String(ts);
  const sec = Math.floor(Math.abs(Date.now() - d.getTime()) / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(sec / 3600);
  const days = Math.floor(sec / 86400);
  if (min < 1) return `Reported in Last ${sec} Seconds`;
  if (hr < 1) return `Reported in Last ${min} Minutes`;
  if (days < 1) return `Reported in Last ${hr} Hours`;
  return `Reported in Last ${days} Days`;
}

function elapsedColor(ts: unknown): string {
  if (ts == null) return '#546e7a';
  const d = new Date(String(ts));
  if (isNaN(d.getTime())) return '#546e7a';
  const min = (Date.now() - d.getTime()) / 60000;
  if (min < 1) return '#4caf50';
  if (min < 5) return '#8bc34a';
  if (min < 30) return '#ff9800';
  return '#f44336';
}

function isActionEnabled(actions: DeviceListAction[] | undefined, type: DeviceListAction['type']): boolean {
  if (!actions) return false;
  return actions.some((a) => a.type === type && a.enabled);
}

function getFeatureCoords(f: GeoJSON.Feature): [number, number] | null {
  const g = f.geometry;
  if (!g) return null;
  if (g.type === 'Point') return [g.coordinates[0], g.coordinates[1]];
  if (g.type === 'MultiPoint' && g.coordinates.length > 0) return [g.coordinates[0][0], g.coordinates[0][1]];
  if (g.type === 'Polygon' && g.coordinates.length > 0) {
    const ring = g.coordinates[0];
    const lng = ring.reduce((s, c) => s + c[0], 0) / ring.length;
    const lat = ring.reduce((s, c) => s + c[1], 0) / ring.length;
    return [lng, lat];
  }
  return null;
}

function openStreetView(lng: number, lat: number) {
  window.open(`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`, '_blank');
}

interface Props {
  widgetConfig?: DeviceListWidgetConfig & { dataBinding?: WidgetDataBinding };
}

const DeviceListWidget: React.FC<Props> = ({ widgetConfig }) => {
  const ctx = useWebMapContext();
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [followingIdx, setFollowingIdx] = useState<number | null>(null);
  const followRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const nameField = widgetConfig?.nameField || 'entity_id';
  const typeField = widgetConfig?.typeField || 'device_type';
  const tsField = widgetConfig?.timestampField || 'timestamp';
  const detailFields = widgetConfig?.detailFields ?? [];
  const formatRules = widgetConfig?.formatRules ?? [];
  const maxItems = widgetConfig?.maxItems ?? 2000;
  const sortDir = widgetConfig?.sortDir ?? 'desc';
  const density = widgetConfig?.density ?? 'comfortable';
  const showSearch = widgetConfig?.showSearch !== false;
  const lineTemplate = widgetConfig?.lineTemplate?.trim() || '';
  const highlightColor = widgetConfig?.highlightColor || '#ffeb3b';
  const selectionMode = widgetConfig?.selectionMode || 'single';
  const actions = widgetConfig?.actions;
  const uniqueField = widgetConfig?.uniqueField;
  const layerId = widgetConfig?.dataBinding?.boundLayerId;

  const hasAction = useCallback((type: DeviceListAction['type']) => isActionEnabled(actions, type), [actions]);

  const features = useMemo(() => {
    if (!layerId || !ctx) return [];
    return ctx.getFeaturesForLayer(layerId);
  }, [layerId, ctx]);

  const sorted = useMemo(() => {
    const arr = [...features];
    arr.sort((a, b) => {
      const va = a.properties?.[tsField] ?? '';
      const vb = b.properties?.[tsField] ?? '';
      const ta = new Date(String(va)).getTime() || 0;
      const tb = new Date(String(vb)).getTime() || 0;
      return sortDir === 'desc' ? tb - ta : ta - tb;
    });
    return arr.slice(0, maxItems);
  }, [features, tsField, sortDir, maxItems]);

  const deduplicated = useMemo(() => {
    if (!uniqueField) return sorted;
    const seen = new Set<string>();
    return sorted.filter((f) => {
      const key = String(f.properties?.[uniqueField] ?? '');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [sorted, uniqueField]);

  const filtered = useMemo(() => {
    if (!search.trim()) return deduplicated;
    const q = search.toLowerCase();
    return deduplicated.filter((f) => {
      const p = f.properties;
      if (!p) return false;
      return Object.values(p).some((v) => v != null && String(v).toLowerCase().includes(q));
    });
  }, [deduplicated, search]);

  const handleSelect = useCallback(
    (idx: number) => {
      if (followRef.current) { clearInterval(followRef.current); followRef.current = null; }
      setFollowingIdx(null);

      setSelectedIds((prev) => {
        let next: Set<number>;
        if (selectionMode === 'multiple') {
          next = new Set(prev);
          if (next.has(idx)) next.delete(idx);
          else next.add(idx);
        } else {
          next = prev.has(idx) ? new Set() : new Set([idx]);
        }

        const selIndices = Array.from(next);
        const selFeatures = selIndices.map((i) => filtered[i]).filter(Boolean);

        if (ctx) {
          if (hasAction('filter') && layerId) {
            if (selFeatures.length > 0) {
              const nameValues = selFeatures.map((f) => f.properties?.[nameField]).filter(Boolean);
              ctx.setCrossFilter(`device_list_${layerId}`, {
                field: nameField,
                value: nameValues.length === 1 ? nameValues[0] : nameValues,
                source: `device_list_${layerId}`,
              });
            } else {
              ctx.removeCrossFilter(`device_list_${layerId}`);
            }
          }

          ctx.setSelectedFeatures(selFeatures);

          if (selFeatures.length === 1) {
            const coords = getFeatureCoords(selFeatures[0]);
            if (coords) {
              if (hasAction('pan')) ctx.flyTo(coords[0], coords[1]);
              if (hasAction('zoom')) ctx.flyTo(coords[0], coords[1], 16);
              if (hasAction('flash')) ctx.flashFeature(coords[0], coords[1]);
            }
          }
        }

        return next;
      });
    },
    [selectionMode, filtered, hasAction, ctx, layerId, nameField],
  );

  const handleActionClick = useCallback(
    (e: React.MouseEvent, action: DeviceListAction['type'], feature: GeoJSON.Feature, idx: number) => {
      e.stopPropagation();
      if (!ctx) return;
      const coords = getFeatureCoords(feature);
      if (!coords) return;
      switch (action) {
        case 'pan':
          ctx.flyTo(coords[0], coords[1]);
          break;
        case 'zoom':
          ctx.flyTo(coords[0], coords[1], 16);
          break;
        case 'flash':
          ctx.flashFeature(coords[0], coords[1]);
          break;
        case 'follow':
          if (followRef.current) { clearInterval(followRef.current); followRef.current = null; }
          if (followingIdx === idx) {
            setFollowingIdx(null);
            break;
          }
          setFollowingIdx(idx);
          ctx.flyTo(coords[0], coords[1], 15);
          followRef.current = setInterval(() => {
            const fresh = getFeatureCoords(feature);
            if (fresh) ctx.flyTo(fresh[0], fresh[1]);
          }, 3000);
          break;
        case 'streetview':
          openStreetView(coords[0], coords[1]);
          break;
      }
    },
    [ctx, followingIdx],
  );

  const renderFormatField = useCallback(
    (rule: DeviceFieldFormatRule, props: Record<string, unknown>) => {
      const raw = resolveField(props, rule.field);
      if (raw == null && rule.displayType !== 'hidden') return null;
      if (rule.displayType === 'hidden') return null;

      const label = rule.label || rule.field;
      const fontSizeMap = { small: 10, normal: 12, large: 15 };
      const styleSx: Record<string, unknown> = {};
      if (rule.bold) styleSx.fontWeight = 700;
      if (rule.italic) styleSx.fontStyle = 'italic';
      if (rule.fontSize && rule.fontSize !== 'normal') styleSx.fontSize = fontSizeMap[rule.fontSize];
      if (rule.textColor) styleSx.color = rule.textColor;
      if (rule.align) styleSx.textAlign = rule.align;
      if (rule.span) { styleSx.width = '100%'; styleSx.flexBasis = '100%'; }

      const hasStyle = Object.keys(styleSx).length > 0;
      const wrap = (el: React.ReactNode) =>
        hasStyle ? <Box key={rule.id} sx={styleSx}>{el}</Box> : el;

      switch (rule.displayType) {
        case 'badge': {
          const val = String(raw ?? '');
          const match = rule.colorMap?.find((c) => c.value === val);
          const bg = match?.bg ?? '#546e7a';
          const fg = match?.fg ?? '#fff';
          return wrap(
            <Chip
              key={rule.id}
              size="small"
              label={val}
              sx={{ fontWeight: rule.bold ? 900 : 700, fontSize: fontSizeMap[rule.fontSize || 'normal'] - 2, bgcolor: bg, color: fg, ...(rule.span && { width: '100%', justifyContent: rule.align === 'center' ? 'center' : rule.align === 'right' ? 'flex-end' : 'flex-start' }) }}
            />,
          );
        }
        case 'elapsed_time': {
          const color = elapsedColor(raw);
          return wrap(
            <Chip
              key={rule.id}
              size="small"
              variant="outlined"
              label={elapsedTimeLabel(raw)}
              sx={{ fontSize: fontSizeMap[rule.fontSize || 'normal'] - 2, fontWeight: 600, color, borderColor: color, ...(rule.span && { width: '100%', justifyContent: rule.align === 'center' ? 'center' : rule.align === 'right' ? 'flex-end' : 'flex-start' }) }}
            />,
          );
        }
        case 'progress': {
          const num = Number(raw) || 0;
          const max = rule.progressMax || 100;
          const pct = Math.min(100, Math.max(0, (num / max) * 100));
          const barColor = pct > 60 ? '#4caf50' : pct > 30 ? '#ff9800' : '#f44336';
          return wrap(
            <Box key={rule.id} sx={{ minWidth: 80, flex: 1 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="caption" fontWeight={600}>{num}{rule.suffix || ''}</Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.08)',
                  '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 2 },
                }}
              />
            </Box>,
          );
        }
        case 'link': {
          const url = rule.linkTemplate
            ? renderTemplate(rule.linkTemplate, props)
            : String(raw ?? '');
          if (!url) return null;
          const linkLabel = rule.label || 'Link';
          return wrap(
            <Typography
              key={rule.id}
              variant="caption"
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{ color: 'primary.main', cursor: 'pointer' }}
            >
              {linkLabel}
            </Typography>,
          );
        }
        case 'boolean': {
          const truthy = raw === true || raw === 'true' || raw === 1 || raw === '1' || raw === 'yes' || raw === 'on';
          const icon = truthy
            ? <CheckIcon sx={{ fontSize: 14, color: rule.trueColor || '#4caf50' }} />
            : <CancelIcon sx={{ fontSize: 14, color: rule.falseColor || '#f44336' }} />;
          const text = truthy ? (rule.trueLabel || 'Yes') : (rule.falseLabel || 'No');
          return wrap(
            <Stack key={rule.id} direction="row" spacing={0.5} alignItems="center">
              {icon}
              <Typography variant="caption" sx={{ fontWeight: 600, color: truthy ? (rule.trueColor || '#4caf50') : (rule.falseColor || '#f44336') }}>
                {text}
              </Typography>
              {label && (
                <Typography variant="caption" color="text.disabled" sx={{ ml: 0.25 }}>
                  ({label})
                </Typography>
              )}
            </Stack>,
          );
        }
        case 'datetime': {
          const d = new Date(String(raw));
          if (isNaN(d.getTime())) return null;
          const fmt = rule.dateFormat || 'datetime';
          let display: string;
          if (fmt === 'date') display = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
          else if (fmt === 'time') display = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
          else if (fmt === 'relative') display = relativeTime(raw);
          else display = d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
          return wrap(
            <Typography key={rule.id} variant="caption" color="text.secondary">
              <span style={{ color: highlightColor, fontWeight: 600 }}>{label}:</span>{' '}
              {display}
            </Typography>,
          );
        }
        case 'threshold': {
          const num = Number(raw);
          if (isNaN(num)) return null;
          const bands = rule.thresholdBands ?? [];
          const sorted = [...bands].sort((a, b) => a.max - b.max);
          let color = '#546e7a';
          let bandLabel = '';
          for (const band of sorted) {
            if (num <= band.max) { color = band.color; bandLabel = band.label || ''; break; }
          }
          if (!bandLabel && sorted.length > 0 && num > sorted[sorted.length - 1].max) {
            color = sorted[sorted.length - 1].color;
          }
          const display = `${rule.prefix || ''}${num}${rule.suffix || ''}`;
          return wrap(
            <Stack key={rule.id} direction="row" spacing={0.5} alignItems="center">
              <DotIcon sx={{ fontSize: 10, color }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color }}>
                {display}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {label}{bandLabel ? ` · ${bandLabel}` : ''}
              </Typography>
            </Stack>,
          );
        }
        case 'tag_list': {
          const sep = rule.tagSeparator ? new RegExp(rule.tagSeparator) : /[,|;]/;
          const tags = String(raw ?? '').split(sep).map((t) => t.trim()).filter(Boolean);
          if (tags.length === 0) return null;
          const bg = rule.tagBg || '#455a64';
          const fg = rule.tagFg || '#fff';
          return wrap(
            <Stack key={rule.id} direction="row" spacing={0.25} flexWrap="wrap" alignItems="center">
              {label && <Typography variant="caption" color="text.disabled" sx={{ mr: 0.5 }}>{label}:</Typography>}
              {tags.map((tag, ti) => (
                <Chip key={ti} size="small" label={tag} sx={{ fontSize: 9, height: 18, bgcolor: bg, color: fg, fontWeight: 600 }} />
              ))}
            </Stack>,
          );
        }
        case 'image': {
          const url = String(raw ?? '');
          if (!url) return null;
          const h = rule.imageHeight || 48;
          return wrap(
            <Box key={rule.id} sx={{ display: 'inline-block' }}>
              <Box
                component="img"
                src={url}
                alt={label}
                sx={{
                  height: h,
                  maxWidth: '100%',
                  borderRadius: 0.5,
                  objectFit: 'cover',
                  display: 'block',
                }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
              />
            </Box>,
          );
        }
        case 'coordinate': {
          const lat = Number(raw);
          const lng = rule.coordField2 ? Number(resolveField(props, rule.coordField2)) : NaN;
          if (isNaN(lat)) return null;
          const display = !isNaN(lng) ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : `${lat.toFixed(6)}`;
          return wrap(
            <Stack key={rule.id} direction="row" spacing={0.5} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                <span style={{ color: highlightColor, fontWeight: 600 }}>{label}:</span>{' '}
                {display}
              </Typography>
              <IconButton
                size="small"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(display);
                }}
                sx={{ p: 0.25 }}
              >
                <CopyIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Stack>,
          );
        }
        case 'number':
        case 'text':
        default: {
          const display = `${rule.prefix || ''}${formatValue(raw)}${rule.suffix || ''}`;
          return wrap(
            <Typography key={rule.id} variant="caption" color="text.secondary">
              <span style={{ color: highlightColor, fontWeight: 600 }}>{label}:</span>{' '}
              {display}
            </Typography>,
          );
        }
      }
    },
    [highlightColor],
  );

  if (!layerId) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Bind this widget to a Web Map layer to display a device / entity list.
        </Typography>
      </Box>
    );
  }

  const compact = density === 'compact';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {showSearch && (
        <Box sx={{ px: 1, pt: 0.5, pb: 0.5, flexShrink: 0 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </InputAdornment>
              ),
              sx: { fontSize: 12 },
            }}
          />
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 1, pb: 1 }}>
        {filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            {search.trim() ? 'No matches.' : 'No features loaded.'}
          </Typography>
        ) : (
          <Stack spacing={compact ? 0.5 : 1}>
            {filtered.map((f, idx) => {
              const p = f.properties ?? {};
              const name = String(p[nameField] ?? `Item ${idx + 1}`);
              const type = String(p[typeField] ?? '');
              const ts = p[tsField];
              const isSelected = selectedIds.has(idx);

              return (
                <Card
                  key={idx}
                  variant="outlined"
                  sx={{
                    borderColor: isSelected ? highlightColor : 'divider',
                    borderWidth: isSelected ? 2 : 1,
                    boxShadow: isSelected ? `0 0 6px ${highlightColor}40` : 0,
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                >
                  <CardActionArea
                    onClick={() => handleSelect(idx)}
                    sx={{ p: compact ? 0.75 : 1.25 }}
                  >
                    {formatRules.length > 0 ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap>
                        {formatRules.map((rule) => renderFormatField(rule, p))}
                      </Stack>
                    ) : lineTemplate ? (
                      <Box
                        sx={{
                          fontSize: compact ? 12 : 13,
                          color: 'text.primary',
                          '& p': { m: 0, lineHeight: 1.6 },
                          '& strong': { color: highlightColor, fontWeight: 700 },
                          '& em': { fontStyle: 'italic' },
                          '& u': { textDecoration: 'underline' },
                          '& s': { textDecoration: 'line-through' },
                          '& h1, & h2, & h3': { m: 0, lineHeight: 1.3 },
                          '& ul, & ol': { m: 0, pl: 2.5 },
                          '& li': { lineHeight: 1.5 },
                          '& img': { maxWidth: '100%', height: 'auto', borderRadius: 0.5 },
                          '& a': { color: 'primary.main' },
                          '& .ql-size-small': { fontSize: '0.8em' },
                          '& .ql-size-large': { fontSize: '1.3em' },
                          '& .ql-size-huge': { fontSize: '1.6em' },
                          '& .ql-indent-1': { pl: 3 },
                          '& .ql-indent-2': { pl: 6 },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: renderTemplate(lineTemplate, p),
                        }}
                      />
                    ) : (
                      <Stack spacing={0.25}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          {widgetConfig?.iconMode === 'symbol' && (
                            <DotIcon sx={{ fontSize: 12, color: badgeColor(type || name) }} />
                          )}
                          <Chip
                            size="small"
                            label={name}
                            sx={{
                              fontWeight: 700,
                              fontSize: compact ? 11 : 13,
                              bgcolor: badgeColor(type || name),
                              color: '#fff',
                            }}
                          />
                          {ts != null && (
                            <Chip
                              size="small"
                              label={relativeTime(ts)}
                              sx={{
                                fontSize: 10,
                                fontWeight: 600,
                                color: ageColor(ts),
                                borderColor: ageColor(ts),
                              }}
                              variant="outlined"
                            />
                          )}
                          {type && (
                            <Typography variant="caption" color="text.secondary">
                              {type}
                            </Typography>
                          )}
                        </Stack>
                        {detailFields.length > 0 ? (
                          <Stack direction="row" spacing={1.5} flexWrap="wrap">
                            {detailFields.map((df) => {
                              const v = p[df];
                              if (v == null) return null;
                              return (
                                <Typography key={df} variant="caption" color="text.secondary">
                                  <span style={{ color: highlightColor, fontWeight: 600 }}>{df}:</span>{' '}
                                  {String(v)}
                                </Typography>
                              );
                            })}
                          </Stack>
                        ) : null}
                      </Stack>
                    )}
                  </CardActionArea>
                  {isSelected && (hasAction('pan') || hasAction('zoom') || hasAction('flash') || hasAction('follow') || hasAction('streetview')) && (
                    <Stack
                      direction="row"
                      spacing={0.25}
                      sx={{ px: 0.75, py: 0.25, borderTop: '1px solid', borderColor: 'divider' }}
                    >
                      {hasAction('pan') && (
                        <Tooltip title="Pan to"><IconButton size="small" onClick={(e) => handleActionClick(e, 'pan', f, idx)}><PanIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                      )}
                      {hasAction('zoom') && (
                        <Tooltip title="Zoom to"><IconButton size="small" onClick={(e) => handleActionClick(e, 'zoom', f, idx)}><ZoomIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                      )}
                      {hasAction('flash') && (
                        <Tooltip title="Flash"><IconButton size="small" onClick={(e) => handleActionClick(e, 'flash', f, idx)}><FlashIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                      )}
                      {hasAction('follow') && (() => {
                        const isFollowing = followingIdx === idx;
                        return (
                          <Tooltip title={isFollowing ? 'Stop following' : 'Follow'}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleActionClick(e, 'follow', f, idx)}
                              sx={{
                                ...(isFollowing && {
                                  bgcolor: 'primary.main',
                                  color: 'primary.contrastText',
                                  animation: 'pulse-follow 1.5s ease-in-out infinite',
                                  '@keyframes pulse-follow': {
                                    '0%, 100%': { boxShadow: '0 0 0 0 rgba(25,118,210,0.4)' },
                                    '50%': { boxShadow: '0 0 0 6px rgba(25,118,210,0)' },
                                  },
                                  '&:hover': { bgcolor: 'primary.dark' },
                                }),
                              }}
                            >
                              <FollowIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        );
                      })()}
                      {hasAction('streetview') && (
                        <Tooltip title="Street View">
                          <IconButton size="small" onClick={(e) => handleActionClick(e, 'streetview', f, idx)}>
                            <StreetViewIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  )}
                </Card>
              );
            })}
          </Stack>
        )}
      </Box>

      <Box sx={{ px: 1, py: 0.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {selectedIds.size > 0 && `${selectedIds.size} selected · `}
          {filtered.length} items{features.length !== filtered.length ? ` (${features.length} total)` : ''}
        </Typography>
      </Box>
    </Box>
  );
};

export default DeviceListWidget;
